<template>
    <div class="data-page">
        <PageHeader title="Usuários" :subtitle="summaryText">
            <template #actions>
                <Button label="Criar Usuário" icon="pi pi-user-plus" @click="openCreateModal" />
            </template>
        </PageHeader>

        <div class="data-card">
            <div class="data-card-toolbar">
                <span class="p-input-icon-left data-search">
                    <i class="pi pi-search" />
                    <InputText type="text" v-model="state.search" placeholder="Buscar por usuário ou login..." />
                </span>
            </div>

            <DataTable class="data-table" paginator :value="filteredUsers" :rowsPerPageOptions="[20, 50, 100]" lazy
                :loading="state.loading.users" :rows="state.perPage" ref="dt" dataKey="id"
                :totalRecords="state.totalRecords" :autoLayout="true" responsiveLayout="scroll"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                @page="onPage($event)">

                <template #empty>
                    <div class="data-empty">
                        <i class="pi pi-inbox"></i>
                        <span>{{ state.search ? 'Nenhum usuário encontrado para essa busca.' : 'Nenhum usuário cadastrado.' }}</span>
                    </div>
                </template>

                <Column field="id" header="ID" class="col-id">
                    <template #body="slotProps">
                        <span class="record-id">{{ formatId(slotProps.data.id) }}</span>
                    </template>
                </Column>
                <Column field="name" header="Usuário">
                    <template #body="slotProps">
                        <span class="record-name">{{ slotProps.data.name }}</span>
                    </template>
                </Column>
                <Column field="login" header="Login">
                    <template #body="slotProps">
                        <span v-if="slotProps.data.login" class="mono-chip" :title="slotProps.data.login">{{ slotProps.data.login }}</span>
                        <span v-else class="empty-value">—</span>
                    </template>
                </Column>
                <Column field="active" header="Status">
                    <template #body="slotProps">
                        <span class="pill pill-status" :class="slotProps.data.active ? 'is-on' : 'is-off'">
                            <span class="pill-dot"></span>
                            {{ changeStatusBoleanToText(slotProps.data.active) }}
                        </span>
                    </template>
                </Column>
                <Column class="col-actions">
                    <template #header>
                        Ações
                    </template>
                    <template #body="slotProps">
                        <div class="flex justify-content-end">
                            <Button class="table-action-button" type="button" icon="pi pi-pencil"
                                @click="openDetailsModal(slotProps.data)"
                                v-tooltip.bottom="{ value: 'Editar', showDelay: 1000, hideDelay: 300 }" />
                        </div>
                    </template>
                </Column>

                <template #paginatorstart>
                    <TableResults :shown="filteredUsers.length" :total="state.totalRecords"
                        :pageTotal="state.users.length" :filtering="!!state.search" />
                </template>
            </DataTable>
        </div>

        <Dialog class="data-dialog" v-model:visible="state.createModal" :style="{ width: '30rem' }" modal
            header="Criar usuário" @update:visible="handleCreateModalClose">

            <form class="data-form-grid data-form-single" @submit.prevent="tryToCreateUser">
                <div class="form-field">
                    <label for="new-user-name">Nome</label>
                    <InputText id="new-user-name" type="text" v-model="state.newUser.name" autofocus />
                </div>

                <div class="form-field">
                    <label for="new-user-login">Usuário</label>
                    <InputText id="new-user-login" type="text" v-model="state.newUser.login" />
                </div>

                <div class="form-field">
                    <label for="new-user-password">Senha</label>
                    <Password inputId="new-user-password" v-model="state.newUser.password" :feedback="false" toggleMask />
                </div>

                <button type="submit" class="hidden"></button>
            </form>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="state.createModal = false" />
                <Button :loading="state.loading.create" type="button" label="Criar usuário" icon="pi pi-check"
                    @click="tryToCreateUser" />
            </template>

        </Dialog>

        <Dialog class="data-dialog" v-model:visible="state.detailsModal" :style="{ width: '32rem' }" modal
            header="Editar usuário">

            <form class="data-form-grid data-form-single" @submit.prevent="saveChanges">

                <div class="data-dialog-subject">
                    <span class="pill pill-muted">ID {{ formatId(state.user.id) }}</span>
                    <span>{{ state.user.name }}</span>
                </div>

                <div class="form-field">
                    <label for="user-name">Nome</label>
                    <InputText id="user-name" type="text" v-model="state.modifiedUser.name" />
                </div>

                <div class="form-field">
                    <label for="user-login">Login</label>
                    <InputText id="user-login" type="text" v-model="state.modifiedUser.login" />
                </div>

                <div class="form-field">
                    <label for="user-status">Status</label>
                    <Dropdown inputId="user-status" v-model="state.modifiedUser.active" optionLabel="name"
                        optionValue="value" :options="activeOptions" />
                </div>

                <div class="form-field">
                    <label for="user-password">Nova senha</label>
                    <Password inputId="user-password" v-model="state.modifiedUser.password" :feedback="false" toggleMask
                        placeholder="Deixe em branco para manter a atual" />
                </div>

                <button type="submit" class="hidden"></button>
            </form>

            <template #footer>
                <Button class="p-button-text" type="button" label="Cancelar" @click="state.detailsModal = false" />
                <Button :loading="state.loading.save" :disabled="!pendingChanges.length" type="button"
                    label="Salvar alterações" icon="pi pi-check" @click="saveChanges" />
            </template>

        </Dialog>
    </div>
</template>

<script>

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Dropdown from 'primevue/dropdown'
import PageHeader from '../../components/PageHeader.vue'
import TableResults from '../../components/TableResults.vue'

import services from '../../services'
import { computed, reactive } from 'vue'

import useNotificationToast from '../../composables/useNotificationToast'
import { useStore } from 'vuex'


export default {

    components: { DataTable, Column, Button, Dialog, InputText, Password, Dropdown, PageHeader, TableResults },

    setup() {
        const state = reactive({
            userFilter: {},

            search: '',

            users: [],
            user: {},
            modifiedUser: {},

            newUser: {
                name: null,
                login: null,
                password: null,
            },

            loading: {
                save: false,
                users: false,
                create: false,
            },

            currentPage: 1,
            perPage: 20,
            totalRecords: 0,

            createModal: false,
            detailsModal: false,

        })

        // o Dropdown guarda booleano para comparar com o valor original da API
        const activeOptions = [
            { name: 'Ativado', value: true },
            { name: 'Desativado', value: false },
        ]

        const { success, error } = useNotificationToast()
        const store = useStore()

        loadUsers()

        function loadUsers() {

            state.loading.users = true

            const params = {
                userFilter: state.userFilter,
                page: state.currentPage,
                perPage: state.perPage,
            }

            const token = store.getters.getToken

            services.users.getAll({ params, token })
                .then((res) => {
                    state.users = res.data.data
                    state.currentPage = res.data.meta.current_page
                    state.totalRecords = res.data.meta.total
                })
                .catch(() => {
                    error("Não foi possível carregar os usuários.")
                })
                .finally(() => { state.loading.users = false })

        }

        function onPage(event) {
            state.currentPage = event.page + 1
            state.perPage = event.rows
            loadUsers()
        }

        const filteredUsers = computed(() => {
            const term = state.search.trim().toLowerCase()

            if (!term) return state.users

            return state.users.filter((user) => {
                return [user.name, user.login, String(user.id)]
                    .some((value) => (value || '').toString().toLowerCase().includes(term))
            })
        })

        const summaryText = computed(() => {
            const total = state.totalRecords
            const parts = [`${total} ${total === 1 ? 'usuário cadastrado' : 'usuários cadastrados'}`]

            // a contagem de ativos só é confiável quando todos os usuários estão na página atual
            if (state.users.length && state.users.length >= total) {
                parts.push(`${state.users.filter((user) => user.active).length} ativos`)
            }

            return parts.join(' · ')
        })

        function formatId(id) {
            return String(id).padStart(2, '0')
        }

        function openCreateModal() {
            clearNewUser()
            state.createModal = true
        }

        function handleCreateModalClose() {
            clearNewUser()
        }

        function clearNewUser() {
            state.newUser.name = null
            state.newUser.login = null
            state.newUser.password = null
        }

        function tryToCreateUser() {

            if (!state.newUser.name || !state.newUser.login || !state.newUser.password) {
                return error('Verifique todos os campos!')
            }

            state.loading.create = true

            const token = store.getters.getToken

            services.users.create({
                name: state.newUser.name,
                login: state.newUser.login,
                password: state.newUser.password,
                token
            }).then(() => {
                success('Usuário criado com sucesso!')
                state.createModal = false
                clearNewUser()
                loadUsers()
            }).catch(() => {
                error('Não foi possível criar o usuário.')
            }).finally(() => {
                state.loading.create = false
            })

        }

        function openDetailsModal(user) {
            state.user = user
            state.modifiedUser = { ...user }
            // a API devolve 1/0; o Dropdown de status trabalha com booleano
            state.modifiedUser.active = !!user.active
            state.modifiedUser.password = null

            state.detailsModal = true
        }

        function changedItems() {
            const changed = []

            for (const key in state.user) {

                if (key === 'password') continue

                const original = state.user[key]
                const modified = state.modifiedUser[key]

                if (original != modified) {
                    changed.push({
                        original,
                        modified,
                        field: key
                    })
                }

            }

            // senha em branco significa manter a atual, não é alteração
            if (state.modifiedUser.password) {
                changed.push({
                    field: 'password'
                })
            }

            return changed
        }

        const pendingChanges = computed(() => changedItems())

        function saveChanges() {

            if (!pendingChanges.value.length) return

            state.loading.save = true

            const modifiedUser = { ...state.modifiedUser }

            modifiedUser.active = modifiedUser.active === 'Ativado' || modifiedUser.active === true ? true : false

            // enviar senha vazia sobrescreveria a senha do usuário
            if (!modifiedUser.password) delete modifiedUser.password

            const token = store.getters.getToken

            services.users.update({ modifiedUser, token }).then(({ data }) => {
                if (String(data.id) === String(store.getters.getUser?.id)) {
                    store.dispatch('setUser', { user: data })
                }

                success('Usuário atualizado com sucesso')
                state.detailsModal = false
                loadUsers()
            }).catch(() => {
                error("Não foi possível atualizar o usuário.")
            }).finally(() => {
                state.loading.save = false
            })

        }

        function changeStatusBoleanToText(active) {
            return active ? 'Ativado' : 'Desativado'
        }

        return {
            state,
            activeOptions,
            filteredUsers,
            summaryText,
            pendingChanges,
            formatId,
            loadUsers,
            onPage,
            openCreateModal,
            handleCreateModalClose,
            tryToCreateUser,
            openDetailsModal,
            saveChanges,
            changeStatusBoleanToText
        }
    }
}
</script>
